import { Sparkles } from 'lucide-react'
import { EVENT } from '@sdg/shared'
import { COUNTRIES, flag } from '../../components/flags'

/**
 * The stage screen's frame — the flag strip, the host nation, the status pill,
 * the award or draw name between two gold rules, and the footer.
 *
 * One component rather than three copies. The Committee's instruction was that
 * the ceremony screens carry the same header and footer as the lucky draw, and
 * that is true by construction here: the draw, the podium and Best Traditional
 * Attire all render through this, so the audience cannot tell that a different
 * component took over between one and the next. Only the palette, the title
 * and the pill differ.
 *
 * Everything is sized in `vmin` because this is projected: the type has to
 * scale with the hall's screen, not sit at a fixed pixel size.
 */

export interface StagePalette {
  /** Full CSS background-image — rays, glow and ground in one. */
  background: string
  border: string
  sparkle: string
  sparkleAlt: string
}

export function StageChrome({
  palette,
  title,
  subtitle,
  status,
  footerLeft,
  children,
}: {
  palette: StagePalette
  title: string
  subtitle?: React.ReactNode
  status: React.ReactNode
  footerLeft?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="h-full w-full min-h-0">
      <section
        /* `select-none`: a stray click on the projector used to leave a name
           sitting in a blue selection highlight for the rest of the award. */
        className="relative isolate flex h-full min-h-0 w-full select-none flex-col overflow-hidden rounded-[1vmin] border-[0.45vmin] px-[2.5vmin] shadow-[0_18px_50px_rgba(11,30,63,0.22)]"
        style={{ backgroundImage: palette.background, borderColor: palette.border }}
      >
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
          <Sparkles
            className="stage-sparkle absolute left-[9%] top-[22%] size-[1.8vmin]"
            style={{ color: palette.sparkle, opacity: 0.55 }}
          />
          <Sparkles
            className="stage-sparkle stage-sparkle-delayed absolute right-[14%] top-[35%] size-[1.3vmin]"
            style={{ color: palette.sparkleAlt, opacity: 0.55 }}
          />
          <Sparkles
            className="stage-sparkle stage-sparkle-delayed-2 absolute bottom-[18%] left-[47%] size-[1.5vmin]"
            style={{ color: palette.sparkle, opacity: 0.45 }}
          />
        </div>

        <header className="relative z-10 shrink-0 border-b border-white/15 pb-[1.2vmin] pt-[0.7vmin]">
          <div className="flex items-center justify-between gap-[2vmin]">
            {/*
              The host institution, first thing on the row.
              
              Left of the flags rather than beside the status badge: the flags
              are the event and this is whose event it is, so it reads as the
              masthead the audience expects on a 3-metre screen. Sized in
              `vmin` like everything else here, so it holds at any projector
              resolution.
            */}
            <img
              src="/lincoln-logo.webp"
              alt="Lincoln University College"
              className="h-[4.4vmin] w-auto shrink-0 rounded-[0.5vmin]"
              draggable={false}
            />

            <div
              className="stage-flags-window min-w-0 flex-1 overflow-hidden"
              aria-label="Flags of the 99 participating countries"
            >
              <div className="stage-flags-marquee flex w-max" aria-hidden="true">
                {[0, 1].map((sequence) => (
                  <div
                    key={sequence}
                    className="flex shrink-0 items-center gap-[0.8vmin] pr-[0.8vmin]"
                  >
                    {COUNTRIES.map((code, index) => (
                      <span
                        key={`${sequence}-${index}-${code}`}
                        className="relative block size-[2.3vmin] shrink-0 overflow-hidden rounded-full border border-white/85 bg-[#E7EFF9] shadow-sm"
                      >
                        <img
                          src={flag(code, 80)}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 block size-full rounded-full object-cover"
                        />
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-[0.8vmin]">
              <span
                aria-label="Host nation: Malaysia"
                className="flex items-center gap-[0.7vmin] rounded-full border border-white/70 bg-white/10 px-[1vmin] py-[0.55vmin] text-[1.3vmin] font-bold uppercase tracking-[0.1em] text-white"
              >
                <img
                  src={flag('my', 80)}
                  alt="Malaysia"
                  className="size-[2.3vmin] rounded-full object-cover ring-[0.15vmin] ring-white/80"
                />
                <span className="hidden sm:inline">Malaysia</span>
              </span>
              <span className="shrink-0 rounded-full bg-[#E6A23C] px-[1.5vmin] py-[0.55vmin] text-[1.45vmin] font-bold uppercase tracking-[0.12em] text-[#152B46]">
                {status}
              </span>
            </div>
          </div>

          <div className="mt-[1vmin] flex items-center justify-center gap-[1vmin]">
            <span className="h-px w-[5vmin] bg-[#E6A23C]/60" />
            <h1 className="text-[3.6vmin] font-extrabold uppercase leading-none tracking-[0.2em] text-[#FFD16A] drop-shadow-[0_3px_14px_rgba(10,25,50,0.45)]">
              {title}
            </h1>
            <span className="h-px w-[5vmin] bg-[#E6A23C]/60" />
          </div>

          {subtitle && (
            <p className="mt-[0.9vmin] text-center text-[1.55vmin] font-semibold uppercase tracking-[0.24em] text-white/80">
              {subtitle}
            </p>
          )}
        </header>

        <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center py-[1.6vmin]">
          {children}
        </div>

        <footer className="relative z-10 flex shrink-0 items-center justify-between gap-[2vmin] border-t border-white/15 pt-[0.55vmin] text-[1.4vmin] text-white/70">
          <span>{footerLeft}</span>
          <span className="flex items-center">
            Technology partner ·{' '}
            <a
              href={EVENT.technologyPartnerUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-[0.3vmin] font-semibold text-white/75"
            >
              {EVENT.technologyPartner}
            </a>
          </span>
        </footer>
      </section>
    </div>
  )
}

/** The event line the approved ceremony layouts carry under the podium. */
export const EVENT_FOOTER = (
  <>
    {EVENT.tagline} <span className="text-white/40">·</span> {EVENT.dateShort}
  </>
)

/** 'Award ceremony · Lincoln International SDG & Cultural Day 2026'. */
export const CEREMONY_SUBTITLE = (
  <>
    Award ceremony <span className="text-white/45">·</span> {EVENT.name}
  </>
)
